/**
 * Storage utilities for photo URLs
 * R2-only: all URLs are direct HTTPS (photos.masterlapse.com)
 */

/**
 * Photo URLs interface - R2 thumbnail sizes
 */
interface PhotoUrls {
  full?: string;
  thumb200?: string;
  thumb400?: string;
  thumb1920?: string;
}

const R2_DOMAIN = "https://photos.masterlapse.com";

/**
 * Derive R2 thumbnail URLs from the full/original URL.
 * Used for newly uploaded photos where thumbnails haven't been set in Firestore yet.
 * The retry mechanism in useImageLoaded will handle loading once thumbnails are generated.
 */
function deriveR2ThumbUrls(fullUrl: string): Partial<PhotoUrls> {
  // Only derive from R2 HTTPS URLs with /original/ path
  if (!fullUrl.startsWith(R2_DOMAIN) || !fullUrl.includes("/original/")) {
    return {};
  }

  return {
    thumb200: fullUrl.replace("/original/", "/thumb200/"),
    thumb400: fullUrl.replace("/original/", "/thumb400/"),
    thumb1920: fullUrl.replace("/original/", "/thumb1920/"),
  };
}

/**
 * Ensure Photo object has complete URLs.
 * - R2 photos with all URLs: pass-through
 * - Newly uploaded R2 photos (missing thumbs): derive from full URL
 * - Legacy gs:// URLs: cleared (files no longer exist in Firebase Storage)
 */
export function convertPhotoUrls<T extends { urls?: PhotoUrls }>(
  photo: T
): T & { urls: PhotoUrls } {
  if (!photo.urls) {
    return { ...photo, urls: {} };
  }

  const urls = photo.urls;

  // Skip gs:// URLs - these are orphaned Firebase Storage references
  if (urls.full && urls.full.startsWith("gs://")) {
    return { ...photo, urls: {} };
  }

  // If thumb200 is missing but full URL exists, derive thumb URLs from R2 pattern
  if (!urls.thumb200 && urls.full) {
    const derived = deriveR2ThumbUrls(urls.full);
    return {
      ...photo,
      urls: { ...urls, ...derived },
    };
  }

  return photo as T & { urls: PhotoUrls };
}
